import { z } from 'zod';

/**
 * Protocole de salle — messages canoniques, conservés à l'identique du prototype
 * (index.html:2077-2098). Le champ discriminant `t` n'est JAMAIS renommé
 * (compatibilité des salles entre versions déployées).
 *
 * - `from` = expéditeur réel du message ; `by` = auteur de l'état transporté.
 * - `v`    = version monotone (LWW), incrémentée à chaque push local.
 * - `puz`  = objet puzzle complet, ou null hors écran de jeu. Relayé opaque.
 */

/** Le serveur ne désérialise pas `puz` au-delà de « objet ou null ». */
const puzSchema = z.record(z.string(), z.unknown()).nullable();

export const stateMsgSchema = z.object({
  t: z.literal('state'),
  from: z.string().min(1).max(64),
  by: z.string().min(1).max(64),
  v: z.number(),
  screen: z.enum(['home', 'hub', 'play']),
  diff: z.number().int().min(0).max(2),
  sizeIdx: z.number().int().min(0).max(2),
  game: z.number().int().min(0).max(7),
  defi: z.number().int().min(0).max(5),
  puz: puzSchema,
});

export const pingMsgSchema = z.object({
  t: z.literal('ping'),
  by: z.string().min(1).max(64),
  need: z.boolean().optional(),
});

export const byeMsgSchema = z.object({
  t: z.literal('bye'),
  by: z.string().min(1).max(64),
});

export const roomMsgSchema = z.discriminatedUnion('t', [
  stateMsgSchema,
  pingMsgSchema,
  byeMsgSchema,
]);

export type StateMsg = z.infer<typeof stateMsgSchema>;
export type PingMsg = z.infer<typeof pingMsgSchema>;
export type ByeMsg = z.infer<typeof byeMsgSchema>;
export type RoomMsg = z.infer<typeof roomMsgSchema>;

/** Partie « snapshot » d'un message state (l'état de partie sérialisable). */
export type Snapshot = Pick<StateMsg, 'screen' | 'diff' | 'sizeIdx' | 'game' | 'defi' | 'puz'>;

/** Parse défensif : retourne le message validé ou null (jamais d'exception). */
export function parseRoomMsg(raw: unknown): RoomMsg | null {
  const res = roomMsgSchema.safeParse(raw);
  return res.success ? res.data : null;
}
